import type { TableFilterValues } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import type {
  CommunicationPlanChannelGroup,
  CommunicationPlanFilterValue,
  CommunicationPlanStatusGroup,
} from "../../../../../_logic/communication-plan-table-contract"

export type CommunicationPlanFeedFilters = {
  searchQuery: string | null
  appointmentDateFrom: string | null
  appointmentDateTo: string | null
  employeeIds: string[]
  channels: string[]
  statusGroups: CommunicationPlanStatusGroup[]
}

export const COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS: CommunicationPlanStatusGroup[] = [
  "DUE",
  "ACTIVE",
  "UNSUCCESSFUL",
]

export const COMMUNICATION_PLAN_STATUS_OPTIONS: Array<{
  name: string
  value: CommunicationPlanStatusGroup
}> = [
  { name: "Geplant", value: "PLANNED" },
  { name: "Anstehend", value: "DUE" },
  { name: "Aktiv", value: "ACTIVE" },
  { name: "Erfolglos", value: "UNSUCCESSFUL" },
]

export const COMMUNICATION_PLAN_CHANNEL_OPTIONS: Array<{
  name: string
  value: CommunicationPlanChannelGroup
}> = [
  { name: "E-Mail", value: "EMAIL" },
  { name: "Mobile Nachricht", value: "MOBILE_MESSAGE" },
  { name: "Telefon", value: "PHONE" },
  { name: "Brief", value: "LETTER" },
]

export const COMMUNICATION_PLAN_CHANNELS: Record<CommunicationPlanChannelGroup, string[]> = {
  EMAIL: ["EMAIL"],
  MOBILE_MESSAGE: ["MOBILE_MESSAGE", "SMS", "RCS", "WHATSAPP"],
  PHONE: ["PHONE", "VOICE", "VOICE_BOT"],
  LETTER: ["LETTER"],
}

const normalizeStringArray = <T extends string>(values: T[] | null | undefined): T[] =>
  Array.from(
    new Set((values ?? []).map((value) => String(value).trim()).filter(Boolean) as T[]),
  ).sort()

const normalizeDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null

  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export const formatCommunicationPlanDate = (
  value: Date | string | null | undefined,
): string | null => {
  const parsed = normalizeDate(value)

  if (!parsed) return null

  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, "0")
  const day = String(parsed.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export const createDefaultCommunicationPlanFilters = (): CommunicationPlanFilterValue => ({
  searchQuery: "",
  appointmentDateRange: { start: null, end: null },
  employeeIds: [],
  channelGroups: [],
  statusGroups: [...COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS],
})

export const normalizeCommunicationPlanFilterValue = (
  value: Partial<CommunicationPlanFilterValue> | null | undefined,
): CommunicationPlanFilterValue => {
  const allowedGroups = new Set(COMMUNICATION_PLAN_CHANNEL_OPTIONS.map((option) => option.value))
  const allowedStatusGroups = new Set(
    COMMUNICATION_PLAN_STATUS_OPTIONS.map((option) => option.value),
  )
  const rawStatusGroups =
    value && Object.prototype.hasOwnProperty.call(value, "statusGroups")
      ? value.statusGroups
      : COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS

  return {
    searchQuery: String(value?.searchQuery ?? "").trim(),
    appointmentDateRange: {
      start: normalizeDate(value?.appointmentDateRange?.start),
      end: normalizeDate(value?.appointmentDateRange?.end),
    },
    employeeIds: normalizeStringArray(value?.employeeIds),
    channelGroups: normalizeStringArray(value?.channelGroups).filter(
      (group): group is CommunicationPlanChannelGroup =>
        allowedGroups.has(group as CommunicationPlanChannelGroup),
    ),
    statusGroups: normalizeStringArray(rawStatusGroups).filter(
      (group): group is CommunicationPlanStatusGroup =>
        allowedStatusGroups.has(group as CommunicationPlanStatusGroup),
    ),
  }
}

export const communicationPlanFiltersActive = (value: CommunicationPlanFilterValue): boolean => {
  const normalized = normalizeCommunicationPlanFilterValue(value)
  const normalizedStatuses = normalizeStringArray(normalized.statusGroups)
  const defaultStatuses = normalizeStringArray(COMMUNICATION_PLAN_DEFAULT_STATUS_GROUPS)

  return Boolean(
    normalized.searchQuery ||
    normalized.appointmentDateRange.start ||
    normalized.appointmentDateRange.end ||
    normalized.employeeIds.length ||
    normalized.channelGroups.length ||
    JSON.stringify(normalizedStatuses) !== JSON.stringify(defaultStatuses),
  )
}

export const communicationPlanExternalFilters = (
  value: CommunicationPlanFilterValue,
): TableFilterValues => {
  const normalized = normalizeCommunicationPlanFilterValue(value)

  return {
    searchQuery: normalized.searchQuery || null,
    appointmentDateFrom: formatCommunicationPlanDate(normalized.appointmentDateRange.start),
    appointmentDateTo: formatCommunicationPlanDate(normalized.appointmentDateRange.end),
    employeeIds: normalized.employeeIds,
    channels: normalizeStringArray(
      normalized.channelGroups.flatMap((group) => COMMUNICATION_PLAN_CHANNELS[group] ?? []),
    ),
    statusGroups: normalized.statusGroups,
  }
}

const stringValue = (value: unknown): string | null => {
  const normalized = Array.isArray(value) ? value[0] : value
  const text = typeof normalized === "string" ? normalized.trim() : ""

  return text || null
}

const arrayValue = (value: unknown): string[] =>
  normalizeStringArray(
    Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === "string")
      : typeof value === "string"
        ? [value]
        : [],
  )

export const communicationPlanFeedFilters = (
  filters: TableFilterValues | null | undefined,
): CommunicationPlanFeedFilters => ({
  searchQuery: stringValue(filters?.searchQuery),
  appointmentDateFrom: stringValue(filters?.appointmentDateFrom),
  appointmentDateTo: stringValue(filters?.appointmentDateTo),
  employeeIds: arrayValue(filters?.employeeIds),
  channels: arrayValue(filters?.channels),
  statusGroups: arrayValue(filters?.statusGroups).filter(
    (value): value is CommunicationPlanStatusGroup =>
      COMMUNICATION_PLAN_STATUS_OPTIONS.some((option) => option.value === value),
  ),
})

export const communicationPlanFeedFilterKey = (filters: CommunicationPlanFeedFilters): string =>
  JSON.stringify({
    searchQuery: filters.searchQuery,
    appointmentDateFrom: filters.appointmentDateFrom,
    appointmentDateTo: filters.appointmentDateTo,
    employeeIds: normalizeStringArray(filters.employeeIds),
    channels: normalizeStringArray(filters.channels),
    statusGroups: normalizeStringArray(filters.statusGroups),
  })
